import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPlayerState } from '../../interfaces/player/IPlayerState';
import api from '../../newApi/apiNet';
import { AxiosError, isAxiosError } from 'axios';
import { RootState } from '../../stores/store';
import { IChannel } from '../../interfaces/IChannel';
import { NotFoundException } from '../../exceptions/notFoundException';
import { playerUIActions } from './playerUISlice';
import { IError } from '../../interfaces/IError.ts';
import { RecommendationService } from '../../services/recommendationService.ts';

const initialPlayerState: IPlayerState = {
    url: '',
    pip: false,
    playing: true,
    controls: true,
    light: false,
    volume: 1.0,
    muted: false,
    played: 0,
    loaded: 0,
    duration: 0,
    playbackRate: 1.0,
    loop: false,
    isBuffer: false,
    errorStatus: null,
    qualityLevel: -1, // -1 === auto quality level
    initialRun: true,
    actualPlayingChannel: null,
};

export const playerSlice = createSlice({
    name: 'player',
    initialState: initialPlayerState,
    reducers: {
        reset: () => {
            return initialPlayerState;
        },
        setIsBuffer: (state: IPlayerState, action: PayloadAction<boolean>) => {
            state.isBuffer = action.payload;
        },
        setErrorStatus: (state: IPlayerState, action: PayloadAction<{
            title: string,
            description?: string
        } | null>) => {
            state.errorStatus = action.payload;
        },
        setInitialRun: (state: IPlayerState, action: PayloadAction<boolean>) => {
            state.initialRun = action.payload;
        },
        setMuted: (state: IPlayerState, action: PayloadAction<boolean>) => {
            state.muted = action.payload;
        },
        setPlaying: (state: IPlayerState, action: PayloadAction<boolean>) => {
            state.playing = action.payload;
        },
        setUrl: (state, action: PayloadAction<string>) => {
            if (action.payload == '')
                state.isBuffer = false;
            state.url = action.payload;
        },
        setVolume: (state, action: PayloadAction<number>) => {
            state.volume = action.payload;
        },
        setQualityLevel: (state, action: PayloadAction<number>) => {
            state.qualityLevel = action.payload;
        },
        togglePlaying: (state: IPlayerState) => {
            state.playing = !state.playing;
        },
        toggleMuted: (state: IPlayerState) => {
            state.muted = !state.muted;
        },
    },
    extraReducers(builder) {
        builder.addCase(changeVideoAsync.fulfilled, (state, action) => {

            const url = `${action.payload.channel.cmd}?token=${action.payload.token}`;
            state.qualityLevel = -1;
            // if (import.meta.env.VITE_BACKEND_TYPE === 'php') {
            //     state.url = 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8';
            // }
            // if (import.meta.env.VITE_BACKEND_TYPE === 'dotnet') {
            //     state.url = url;
            // }
            state.url = url;
            state.actualPlayingChannel = action.payload.channel;
            state.playing = true;
        });
        builder.addCase(changeVideoAsync.rejected, (state, action) => {
            const axiosError = action.payload as AxiosError | Error;
            if (isAxiosError(axiosError)) {
                const error = axiosError?.response?.data as IError;
                if (axiosError.response?.status === 401) {
                    state.errorStatus = {
                        title: 'Twoja sesja wygasła, zaloguj sie ponownie',
                    };
                } else if (axiosError.response?.status === 403) {
                    if (error.MsgCode === 'CHANNEL_NOT_AVAILABLE_IN_OUTSIDE_NETWORK')
                        state.errorStatus = {
                            title: 'Kanał dostępny tylko w sieci wewnętrznej Beskid Media',
                        };
                    else if (error.MsgCode === 'CHANNEL_NOT_AVAILABLE_IN_TARIFF')
                        state.errorStatus = {
                            title: 'Kanał nie jest dostępny w twoim pakiecie',
                        };
                    else if (error.MsgCode === 'ACCESS_DENIED_FROM_THIS_COUNTRY')
                        state.errorStatus = {
                            title: 'Kanał nie jest dostępny poza terytorium Polski.',
                        };
                    else if (error.MsgCode === 'DEVICE_LIMIT_REACHED')
                        state.errorStatus = {
                            title: 'Przekroczono limit urządzeń, na których można jednocześnie oglądać.',
                        };
                    else
                        state.errorStatus = { title: 'Nieautoryzowany dostęp' };
                } else {
                    state.errorStatus = { title: 'Coś poszło nie tak...' };
                }
            }
            state.url = '';
        });
    },
});

export const changeVideoAsync = createAsyncThunk<{ channel: IChannel, token: string }, { chId?: number }, {
    state: RootState
}>(
    'player/change-video',
    async ({ chId }, { rejectWithValue, getState, dispatch }) => {
        let channel: IChannel | undefined;
        const _recommendationService = new RecommendationService();
        const channelToChangeIndex = getState().playerUI.channelToChangeIndex;
        const channels = getState().playerUI.channels;
        if (chId) {
            const channelTemp = channels.find(x => x.ch_id === chId);
            if (channelTemp)
                channel = {
                    ch_id: channelTemp.ch_id,
                    cmd: channelTemp.cmd,
                    enabled: channelTemp.enabled,
                    epgList: channelTemp.epgList,
                    genre_id: 0,
                    genre_name: '',
                    logo: channelTemp.logo,
                    name: channelTemp.name,
                    position: 0,
                };
        } else {
            const channelTemp = channels[channelToChangeIndex];
            channel = {
                ch_id: channelTemp.ch_id,
                cmd: channelTemp.cmd,
                enabled: channelTemp.enabled,
                epgList: channelTemp.epgList,
                genre_id: 0,
                genre_name: '',
                logo: channelTemp.logo,
                name: channelTemp.name,
                position: 0,
            };
        }
        try {
            if (channel) {
                const token = await api.getToken(channel.ch_id);
                _recommendationService.addVisitedCountOnChannel(channel.ch_id);
                return { channel, token: token.data };
            }
            throw NotFoundException('CHANNEL_NOT_FOUND', 'Not found channel');
        } catch (error) {
            return rejectWithValue(error);
        } finally {
            if (channel) {
                dispatch(playerUIActions.setActualChannelIndex(channelToChangeIndex));
                dispatch(playerUIActions.setChannelToChangeIndex(channelToChangeIndex));
            } else {
                dispatch(playerUIActions.setActualChannelIndex(1));
                dispatch(playerUIActions.setChannelToChangeIndex(1));
            }
            dispatch(playerUIActions.setActualEpgIndexToActualTime());
        }
    },
);

// export const changeVideoByChIdAsync = createAsyncThunk<string, { chId: number, url: string }>(
//     "player/change-video",
//     async ({chId, url}, {rejectWithValue}) => {
//         try {
//             const token = await api.getToken(chId);
//             return `${url}?token=${token.data}`;
//         } catch (error) {
//             return rejectWithValue(error);
//         }
//     }
// );

export const playerActions = playerSlice.actions;
export default playerSlice.reducer;

